// The final guard at the protected Beta route boundary.
//
// App.js no longer imports the Beta page components at all, so an unauthorized
// Cortex session cannot download those chunks and there is nothing to mount.
// This component is what the protected paths resolve to instead: it decides
// BEFORE rendering, so a denied session produces no protected component, no
// effect, no data fetch and no partial shell — not even for one frame.
//
// It also covers the paths a click never takes: a typed URL, a bookmark, a
// deep link, Back/Forward, history.go(), a refresh, and a page restored from
// the browser's back/forward cache.

import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  BETA_DENIAL_LANDING_PATH,
  isProtectedBetaRoute,
  resolveBetaSurfaceAccess,
} from "../config/betaSurfaceAccess";

/**
 * Global history/bfcache guard. Mounted once, high in the tree, so it keeps
 * working on transitions that never re-run a route element.
 *
 * A page restored from the back/forward cache comes back with its DOM and
 * JavaScript heap intact — React does not necessarily re-render, so a route
 * guard alone can leave previously-rendered privileged content on screen. When
 * `pageshow` reports a persisted restore on a protected path we leave the
 * document entirely rather than trusting the restored tree.
 */
export function useBetaSurfaceHistoryGuard(role) {
  const permitted = resolveBetaSurfaceAccess(role);

  useEffect(() => {
    if (permitted) return undefined;
    if (typeof window === "undefined") return undefined;

    const evict = () => {
      if (!isProtectedBetaRoute(window.location?.pathname)) return;
      // replace() so the protected entry does not stay in the history stack
      // for Forward to walk back into.
      window.location.replace(BETA_DENIAL_LANDING_PATH);
    };

    const onPageShow = (event) => {
      // `persisted` marks a back/forward-cache restore. Re-check either way:
      // a restored tab may also have changed session since it was hidden.
      if (event?.persisted) evict();
      else if (isProtectedBetaRoute(window.location?.pathname)) evict();
    };

    const onPopState = () => evict();

    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("popstate", onPopState);
    };
  }, [permitted]);
}

/**
 * Route element for every protected Beta path.
 *
 * Renders a redirect for any principal the canonical contract does not
 * permit — which, in Cortex, is every principal. `children` exists so the
 * boundary stays a real guard rather than an unconditional redirect: if a
 * permitted principal is ever introduced, the guard is already the thing
 * deciding.
 */
const BetaSurfaceRoute = ({ children = null }) => {
  const location = useLocation();
  const auth = useAuth() || {};

  if (!resolveBetaSurfaceAccess(auth.role)) {
    return (
      <Navigate
        to={BETA_DENIAL_LANDING_PATH}
        replace
        state={{ betaSurfaceUnavailable: true, from: location.pathname }}
      />
    );
  }

  return children;
};

export default BetaSurfaceRoute;
